import React, { useState } from "react";
import { LuLogOut, LuPlus } from "react-icons/lu";
import { useAuth0 } from "@auth0/auth0-react";
import { PageLayout } from "../../app/PageLayout";
import { Badge, Button, ConfirmDialog } from "../../components/ui";
import { planDetails } from "../../constants/freePlan";
import { planoChaveLabel } from "../../constants/billing";
import { useSession } from "../../context/SessionContext";
import { useAccess } from "../../hooks/useAccess";
import { useEspecialidades } from "../../hooks/useEspecialidades";
import { t } from "../../i18n";
import { AnalyzingOverlay } from "./AnalyzingOverlay";
import { ConsultationRecorder } from "./ConsultationRecorder";
import { DictationButton } from "./DictationButton";
import { OrderField } from "./OrderField";
import { MAX_LAUDOS, useNewOrder } from "./useNewOrder";

const joinFields = (fields: string[]) =>
  fields.length > 1 ? `${fields.slice(0, -1).join(", ")}${t("newOrder.and")}${fields[fields.length - 1]}` : fields[0];

export const NewOrderPage: React.FC = () => {
  const order = useNewOrder();
  const { metadata, billing } = useSession();
  const { canUseVoice } = useAccess();
  const { logout } = useAuth0();
  const [confirmLogout, setConfirmLogout] = useState(false);
  const specialty = useEspecialidades().find((e) => e.chave === metadata.especialidade)?.nome;
  const planKey = billing?.plano_chave ?? billing?.plano?.plano_chave;
  const planLabel = planKey ? planoChaveLabel(planKey) : planDetails.free.shortTitle;

  const dictation = (field: string, onText: (text: string) => void) =>
    canUseVoice ? <DictationButton field={field} onText={onText} /> : undefined;

  return (
    <PageLayout
      title={t("newOrder.title")}
      action={
        <Button variant="outline" size="sm" icon={<LuLogOut size={18} aria-hidden />} onClick={() => setConfirmLogout(true)} aria-label={t("newOrder.logoutLabel")}>
          <span className="hidden min-[381px]:inline">{t("newOrder.logout")}</span>
        </Button>
      }
      cta={
        <Button variant="primary" size="lg" disabled={order.missing.length > 0} onClick={order.analyze}>
          {t("newOrder.analyze")}
        </Button>
      }
      ctaHint={order.missing.length ? t("newOrder.missing", { fields: joinFields(order.missing) }) : undefined}
    >
      <p className="pt-2 flex flex-wrap items-center gap-2 text-label text-fg-muted">
        {specialty || t("newOrder.noSpecialty")}
        <Badge tone="primary">{planLabel}</Badge>
      </p>

      <ConsultationRecorder onFields={order.applyConsultation} />

      <p className="flex items-center gap-3 text-caption font-medium text-fg-subtle before:h-px before:flex-1 before:bg-line after:h-px after:flex-1 after:bg-line">
        {t("newOrder.manual")}
      </p>

      <OrderField
        number={1}
        label={t("newOrder.patientName")}
        value={order.patientName}
        onChange={order.setPatientName}
        placeholder={t("newOrder.patientNamePlaceholder")}
        dictation={dictation(t("newOrder.missingName"), order.setPatientName)}
      />
      <OrderField
        number={2}
        label={t("newOrder.history")}
        value={order.history}
        onChange={order.setHistory}
        rows={3}
        placeholder={t("newOrder.historyPlaceholder")}
        hint={t("newOrder.historyHint")}
        dictation={dictation(t("newOrder.missingHistory"), order.setHistory)}
      />
      {order.laudos.map((laudo, i) => (
        <OrderField
          key={i}
          number={i + 3}
          label={
            <>
              {t("newOrder.laudo")}
              {order.laudos.length > 1 && <span className="font-normal text-fg-subtle"> {t("newOrder.laudoNumber", { n: i + 1 })}</span>}
            </>
          }
          value={laudo}
          onChange={(value) => order.setLaudo(i, value)}
          rows={2}
          placeholder={t("newOrder.laudoPlaceholder")}
          dictation={dictation(t("newOrder.missingLaudo"), (text) => order.setLaudo(i, text))}
          onRemove={i > 0 ? () => order.removeLaudo(i) : undefined}
          removeLabel={t("newOrder.removeLaudo", { n: i + 1 })}
        />
      ))}

      {order.laudos.length < MAX_LAUDOS ? (
        <Button variant="text" className="self-start" icon={<LuPlus size={18} aria-hidden />} onClick={order.addLaudo}>
          {t("newOrder.addLaudo")}
        </Button>
      ) : (
        <p className="text-caption text-fg-subtle">{t("newOrder.maxLaudos", { n: MAX_LAUDOS })}</p>
      )}

      {order.analyzing && <AnalyzingOverlay onCancel={order.cancel} />}
      {confirmLogout && (
        <ConfirmDialog
          title={t("newOrder.logoutTitle")}
          message={t("newOrder.logoutBody")}
          confirmLabel={t("newOrder.logoutConfirm")}
          tone="default"
          onCancel={() => setConfirmLogout(false)}
          onConfirm={() => {
            sessionStorage.clear(); // o rascunho tem dados do paciente
            logout({ logoutParams: { returnTo: `${window.location.origin}/auth-callback` } });
          }}
        />
      )}
    </PageLayout>
  );
};
